import React, { useState } from "react";
import styled from "styled-components";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";
import DrawerModal from "./ManagementModal";
import _ from "lodash";
import BigBlueButton from "../../../pages/CaseDetails/common/BigBlueButton";
import AddIcon from "@material-ui/icons/Add";
import useUser from "../../../../hooks/useUser";
import { getFileName } from "../../../../helpers/functions";

const TribunalSection = ({
  caseDetails,
  isActionsDisabled,
  meetingDetail,
  addCaseManager = () => {},
  removeCaseManager = () => {},
  removeJoinCm = () => {},
  currentState,
  setOpenAssignCaseManager,
  isActionsCmDisabled,
}) => {
  const [modalData, setModalData] = useState(null);
  const { authSetting } = useUser();

  const mediators = caseDetails?.requestedMediators || [];
  const presidingArbitrator =
    mediators.find((m) => m.presidingArbitrator === "yes") || {};
  const associateArbitrators = mediators.filter(
    (m) => m.id !== presidingArbitrator.id,
  );

  const primaryCaseManager = caseDetails?.caseManager || {};
  const associateCaseManagers = caseDetails?.caseManagerIds || [];

  const isCmDisabled =
    caseDetails?.resolutionKind !== "negotiation" &&
    (caseDetails?.status === "draft" || primaryCaseManager?.disabled);

  const showRemoveLead = currentState?.index < 4 && !!primaryCaseManager?.id;

  const getInitials = (name) => {
    if (!name) return "NA";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const openModal = (person, type) => {
    setModalData({ person, type });
  };

  const filteredAssociateCaseManagers = associateCaseManagers.filter(
    (coManager) => coManager?.id !== primaryCaseManager?.id,
  );

  const data = {
    tribunal: {
      presiding: {
        ...presidingArbitrator,
        name: presidingArbitrator.name || "",
        role: "Presiding Arbitrator",
        appointedDate: presidingArbitrator.date
          ? new Date(
              presidingArbitrator.date
                .split(" ")[0]
                .split("/")
                .reverse()
                .join("-"),
            ).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "N/A",
      },
      associates: associateArbitrators.slice(0, 3).map((m, i) => ({
        ...m,
        initials: getInitials(m.name),
        color: ["#2c3e50", "#8e44ad", "#e74c3c", "#f39c12"][i % 4],
      })),
      associatesCount: associateArbitrators.length,
      associatesData: associateArbitrators,
    },
    caseManagement: {
      lead: {
        initials: getInitials(primaryCaseManager.name),
        name: primaryCaseManager.name || "Lead Case Manager",
        email: primaryCaseManager?.email,
        mobile: primaryCaseManager?.mobile,
        role: "Lead Case Manager",
      },
      associates: filteredAssociateCaseManagers.slice(0, 4).map((cm, i) => ({
        ...cm,
        initials: getInitials(cm.name),
        color: ["#2c3e50", "#8e44ad", "#e74c3c", "#f39c12"][i % 4],
      })),
      associatesOverflow: Math.max(0, filteredAssociateCaseManagers.length - 4),
      associatesCount: filteredAssociateCaseManagers.length,
      associatesData: filteredAssociateCaseManagers,
    },
    seat: caseDetails?.seat || "Chennai",
    rules:
      authSetting.rulesDocumentUrl ||
      "Arbitration & Conciliation Act, 1996 (as amended)",
  };

  const visibleAssociateNames = data.caseManagement.associatesData.slice(0, 5);
  const hiddenAssociateCount = Math.max(
    0,
    data.caseManagement.associatesCount - visibleAssociateNames.length,
  );

  const handleAddAssociateCm = () => {
    if (isCmDisabled) return;

    const hasLeadCaseManager = !!primaryCaseManager?.name;

    if (hasLeadCaseManager) {
      addCaseManager({ state: true, role: "associateCm" });
    } else {
      addCaseManager({ state: true });
    }
  };

  const handleRemoveLeadCm = () => {
    if (isCmDisabled) return;
    removeCaseManager({ state: true });
  };

  const handleRemoveAssociateCm = (coCaseManager) => {
    const isAssocDisabled =
      caseDetails?.resolutionKind !== "negotiation" &&
      (caseDetails?.status === "draft" || coCaseManager?.disabled);
    if (isAssocDisabled) return;
    removeJoinCm(coCaseManager, caseDetails);
  };

  return (
    <SectionWrapper>
      <MainContainer>
        <LeftPanel>
          <Card>
            <InfoLabel>Arbitrator(s)</InfoLabel>

            <CaseManagementRow>
              {data.tribunal.presiding.name && (
                <LeadManager>
                  {data.tribunal.presiding.avatarUrl ? (
                    <CaseManagerProfile
                      src={data.tribunal.presiding.avatarUrl}
                    />
                  ) : (
                    <InitialsCircle color="#00a884">
                      {getInitials(data.tribunal.presiding.name)}
                    </InitialsCircle>
                  )}
                  <div style={{ flex: 1 }}>
                    <Name
                      onClick={() =>
                        openModal(data.tribunal.presiding, "Arbitrator")
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {data.tribunal.presiding.name}
                    </Name>
                    <Designation>
                      Presiding Arbitrator · Appointed{" "}
                      {data.tribunal.presiding.appointedDate}
                    </Designation>
                  </div>
                </LeadManager>
              )}

              {data.tribunal.associatesCount > 0 && (
                <AssociatesSection>
                  <AvatarGroup>
                    {data.tribunal.associates.map((assoc, i) =>
                      assoc?.avatarUrl ? (
                        <CaseManagerProfile key={i} src={assoc.avatarUrl} />
                      ) : (
                        <MiniAvatar key={i} color={assoc.color}>
                          {assoc.initials}
                        </MiniAvatar>
                      ),
                    )}
                  </AvatarGroup>
                  <AssociatesInfo>
                    <AssociateCount>
                      {data.tribunal.associatesCount} Associate Arbitrator
                      {data.tribunal.associatesCount > 1 ? "s" : ""}
                    </AssociateCount>
                    <AssociateNames>
                      {data.tribunal.associatesData.map((cm, index) => (
                        <React.Fragment key={cm.id || index}>
                          <AssociateLink
                            onClick={() => openModal(cm, "Arbitrator")}
                          >
                            {cm.name}
                          </AssociateLink>
                          {index !== data.tribunal.associatesData.length - 1 &&
                            " · "}
                        </React.Fragment>
                      ))}
                    </AssociateNames>
                  </AssociatesInfo>
                </AssociatesSection>
              )}
            </CaseManagementRow>

            <Divider />

            <InfoLabel
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              CASE MANAGEMENT
            </InfoLabel>

            {caseDetails?.resolutionKind !== "negotiation" && (
              <CaseManagementRow>
                <LeadManager>
                  {primaryCaseManager.avatarUrl ? (
                    <CaseManagerProfile src={primaryCaseManager.avatarUrl} />
                  ) : (
                    <InitialsCircle color="#00a884">
                      {data.caseManagement.lead.initials}
                    </InitialsCircle>
                  )}
                  <div style={{ flex: 1 }}>
                    <Name
                      onClick={() =>
                        openModal(primaryCaseManager, "Case Manager")
                      }
                      style={{ cursor: "pointer" }}
                    >
                      {primaryCaseManager.name || "Lead Case Manager"}
                    </Name>
                    <Designation>Lead Case Manager</Designation>
                  </div>
                </LeadManager>

                {data.caseManagement.associatesCount > 0 && (
                  <AssociatesSection>
                    <AvatarGroup>
                      {data.caseManagement.associates.map((assoc, i) =>
                        assoc?.avatarUrl ? (
                          <CaseManagerProfile key={i} src={assoc.avatarUrl} />
                        ) : (
                          <MiniAvatar key={i} color={assoc.color}>
                            {assoc.initials}
                          </MiniAvatar>
                        ),
                      )}
                      {data.caseManagement.associatesOverflow > 0 && (
                        <OverflowAvatar>
                          +{data.caseManagement.associatesOverflow}
                        </OverflowAvatar>
                      )}
                    </AvatarGroup>
                    <AssociatesInfo>
                      <AssociateCount>
                        {data.caseManagement.associatesCount} Associate Case
                        Manager
                        {data.caseManagement.associatesCount > 1 ? "s" : ""}
                      </AssociateCount>
                      <AssociateNames>
                        {visibleAssociateNames.map((cm, index) => {
                          const isAssocDisabled =
                            caseDetails?.resolutionKind !== "negotiation" &&
                            (caseDetails?.status === "draft" || cm?.disabled);
                          const showRemoveAssoc =
                            currentState?.index < 4 && !!cm?.id;

                          return (
                            <React.Fragment key={cm.id || index}>
                              <AssociateLink
                                onClick={() =>
                                  openModal(cm, "Associate Case Managers")
                                }
                              >
                                {cm.name}
                              </AssociateLink>
                              {index !== visibleAssociateNames.length - 1 &&
                                " · "}
                            </React.Fragment>
                          );
                        })}
                        {hiddenAssociateCount > 0 && (
                          <MoreCount>{` +${hiddenAssociateCount} more`}</MoreCount>
                        )}
                      </AssociateNames>
                    </AssociatesInfo>
                  </AssociatesSection>
                )}
              </CaseManagementRow>
            )}
          </Card>
        </LeftPanel>

        <RightPanel>
          <Card>
            <InfoLabel>RULES</InfoLabel>
            <HyperLink onClick={() => window.open(data.rules)}>
              {getFileName(data.rules)}
            </HyperLink>
          </Card>
          {caseDetails?.resolutionKind !== "negotiation" && (
            <Card>
              <BigBlueButton
                id={caseDetails?.id}
                caseDetails={caseDetails}
                isActionsDisabled={isActionsDisabled}
                status={caseDetails?.status}
                resolutionKind={caseDetails?.resolutionKind}
                type={`Join ${_.capitalize(caseDetails?.resolutionKind)} Session`}
              />
            </Card>
          )}
        </RightPanel>
      </MainContainer>

      {modalData && (
        <DrawerModal
          modal={!!modalData}
          setModal={() => setModalData(null)}
          id={modalData.person?.id}
          type={modalData.type}
          details={modalData.person}
        />
      )}
    </SectionWrapper>
  );
};

export default TribunalSection;

const SectionWrapper = styled.div`
  padding: 0px 0px 16px;
  background: #f8fafc;
  border-radius: 12px;
`;

const MainContainer = styled.div`
  display: flex;
  gap: 12px;
  min-width: 0;

  & .add_circle {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    cursor: pointer;
    background-color: rgba(0, 131, 140, 0.13);
    color: ${COLORS.BTN_GREEN};
    font-family: ${theme?.fonts?.primaryFontSemiBold};
  }

  @media (max-width: 1100px) {
    flex-direction: column;
  }
`;

const LeftPanel = styled.div`
  flex: 2;
  min-width: 0;
`;

const RightPanel = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Card = styled.div`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
  min-width: 0;
`;

const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 20px 0;
`;

const CaseManagementRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const LeadManager = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 12px;
  border-right: 1px solid #e2e8f0;
  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
    padding-bottom: 12px;
  }
`;

const AssociatesSection = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding-left: 12px;
  @media (max-width: 768px) {
    width: 100%;
    padding-left: 0;
    padding-top: 8px;
  }
`;

const InitialsCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) => props.color || "#00a884"};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
`;

const CaseManagerProfile = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid white;
  flex-shrink: 0;
`;

const Name = styled.div`
  font-size: 14.5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  cursor: pointer;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  &:hover {
    color: ${COLORS.BTN_GREEN};
    text-decoration: underline;
  }
`;

const Designation = styled.div`
  font-size: 12.5px;
  color: ${COLORS.INPUT_LABEL};
  margin-top: 2px;
`;

const AvatarGroup = styled.div`
  display: flex;
  gap: -8px;
  align-items: center;
  margin-top: 4px;
`;

const MiniAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${(props) => props.color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  margin-left: -8px;
  border: 3px solid white;
  flex-shrink: 0;
`;

const OverflowAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  margin-left: -8px;
  border: 3px solid white;
  flex-shrink: 0;
`;

const AssociatesInfo = styled.div`
  min-width: 0;
`;

const AssociateCount = styled.div`
  font-size: 13.5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
`;

export const AssociateNames = styled.div`
  font-size: 12px;
  color: #64748b;
  margin-top: 1px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  overflow-wrap: anywhere;
`;

export const AssociateLink = styled.span`
  cursor: pointer;
  color: #64748b;
  &:hover {
    color: ${COLORS.BTN_GREEN};
    text-decoration: underline;
  }
`;

const MoreCount = styled.span`
  color: #475569;
  font-size: 12px;
  align-self: center;
`;

export const AddButton = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  border-radius: 999px;
  color: ${COLORS.BTN_GREEN};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  white-space: nowrap;

  @media (max-width: 768px) {
    white-space: normal;
  }
`;

export const InfoLabel = styled.p`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #7a8fa3;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 8px 0;
  font-weight: 500;
`;

export const InfoValue = styled.p`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.COLOR_DARK};
  font-size: 15px;
  margin: 0;
  word-break: break-word;
`;

const HyperLink = styled.p`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${COLORS.BTN_GREEN};
  font-size: 15px;
  margin: 0;
  cursor: pointer;
  word-break: break-word;
`;

export const CloseBtn = styled.button`
  width: 34px;
  height: 34px;
  border: none;
  background: #f4f6f8;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.18s;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    color: #ef4444;
    transform: rotate(90deg);
  }
`;

const RemoveBtn = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: #fee2e2;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ef4444;
  font-size: 11px;
  flex-shrink: 0;
  transition: all 0.18s;
  margin-left: auto;
  &:hover {
    background: #fca5a5;
  }
`;

const RemoveInlineBtn = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  color: #ef4444;
  font-size: 10px;
  padding: 0 2px;
  vertical-align: middle;
`;
